import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface QuickWin {
  flowId: string;
  flowName: string;
  title: string;
  description: string;
  estimatedMinutes: number;
  points: number;
}

interface QuickWinsWidgetProps {
  quickWins: QuickWin[];
  selectedOrgId: string | null;
}

const formatMinutes = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

export function QuickWinsWidget({ quickWins, selectedOrgId }: QuickWinsWidgetProps) {
  const navigate = useNavigate();
  const [quickWinsExpanded, setQuickWinsExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Group quick wins by title
  const groupedQuickWins = quickWins.reduce((groups, win) => {
    if (!groups[win.title]) {
      groups[win.title] = [];
    }
    groups[win.title].push(win);
    return groups;
  }, {} as Record<string, QuickWin[]>);

  // Convert to array and sort by total points (highest first)
  const sortedQuickWinGroups = Object.entries(groupedQuickWins)
    .map(([title, wins]) => ({
      title,
      wins,
      totalPoints: wins.reduce((sum, w) => sum + w.points, 0),
      totalMinutes: wins.reduce((sum, w) => sum + w.estimatedMinutes, 0)
    }))
    .sort((a, b) => b.totalPoints - a.totalPoints)
    .slice(0, 10); // Show top 10 groups

  const toggleGroup = (title: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(title)) {
      newExpanded.delete(title);
    } else {
      newExpanded.add(title);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <div className="mt-8 bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Quick Wins - Top Improvements</h3>
            <p className="text-sm text-gray-500">Easy fixes that can improve your flow health quickly</p>
          </div>
          <div className="flex items-center space-x-4">
            {quickWins.length > 0 && (
              <p className="text-sm text-gray-600">
                Total: {quickWins.length} quick wins across {sortedQuickWinGroups.length} issue{sortedQuickWinGroups.length !== 1 ? 's' : ''} • 
                {' '}{quickWins.reduce((sum, w) => sum + w.points, 0)} points • 
                {' '}~{formatMinutes(quickWins.reduce((sum, w) => sum + w.estimatedMinutes, 0))}
              </p>
            )}
            <button
              onClick={() => setQuickWinsExpanded(!quickWinsExpanded)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className={`w-5 h-5 transform transition-transform ${quickWinsExpanded ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      {quickWinsExpanded && (
        <div className="p-6">
          {sortedQuickWinGroups.length > 0 ? (
            <div className="space-y-4">
            {sortedQuickWinGroups.map((group, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h4 className="text-sm font-medium text-gray-900">{group.title}</h4>
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                          {group.totalPoints} pts
                        </span>
                        {group.wins.length > 1 && (
                          <span className="ml-2 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                            {group.wins.length} flows
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{group.wins[0].description}</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <p className="text-sm text-gray-600">
                        ~{formatMinutes(group.wins.reduce((sum, w) => sum + w.estimatedMinutes, 0))}
                      </p>
                      <button
                        onClick={() => toggleGroup(group.title)}
                        className="ml-4 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
                      >
                      <span>View {group.wins.length === 1 ? 'Issue' : 'Issues'}</span>
                      <svg 
                        className={`ml-2 h-4 w-4 transform transition-transform ${expandedGroups.has(group.title) ? '' : 'rotate-180'}`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    </div>
                  </div>
                </div>
                {expandedGroups.has(group.title) && (
                  <div className="border-t border-gray-200 bg-white">
                    <div className="divide-y divide-gray-200">
                      {group.wins.map((win, winIndex) => (
                        <div key={winIndex} className="px-4 py-3 hover:bg-gray-50 flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{win.flowName}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Est. {formatMinutes(win.estimatedMinutes)} • {win.points} pts
                            </p>
                          </div>
                          <button
                            onClick={() => navigate(`/${selectedOrgId}/flow/${win.flowId}`)}
                            className="ml-4 text-sm text-blue-600 hover:text-blue-800"
                          >
                            View Flow →
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          ) : (
            <p className="text-center text-gray-500">No quick wins available at the moment</p>
          )}
        </div>
      )}
    </div>
  );
}